import { createDisplayName } from '@withjoy/joykit/utils';
import { mergeOverrides } from '@shared/utils/overrides';
import React from 'react';
import type { PopoverV2Props } from '../PopoverV2';
import { PopoverV2, usePopoverContext } from '../PopoverV2';
import { Backdrop } from '../Backdrop';
import { Portal } from '../Portal';

const TOOLTIP_OVERRIDES: PopoverV2Props['overrides'] = {
  Content: { props: { paddingX: 5, paddingY: 4, backgroundColor: 'black', color: 'white' } },
  Arrow: { props: { backgroundColor: 'black' } },
};

const defaultProps = {
  triggerType: 'hover',
  placement: 'bottom',
  showArrow: true,
  showBackdrop: false,
} as const;

// Do not let an exit spring leave the previous help tip on screen during a handoff.
const HoverTooltipContent: React.FC = ({ children }) => {
  const { isOpen, setVisibilityForKey } = usePopoverContext();
  React.useEffect(() => {
    // Unmounting skips the exit spring's final frame; release its visibility latch
    // so Popper can dispose of the removed content and its scroll/resize listeners.
    if (!isOpen) setVisibilityForKey('content', false);
  }, [isOpen, setVisibilityForKey]);
  return isOpen ? <PopoverV2.Content>{children}</PopoverV2.Content> : null;
};

export type TooltipV2Props = Readonly<
  PopoverV2Props & {
    content: React.ReactNode;
    showBackdrop?: boolean;
    /** When true, renders tooltip content in a portal to escape overflow clipping */
    usePortal?: boolean;
  }
>;

export const TooltipV2 = (props: TooltipV2Props) => {
  const { children: childrenProp, content, overrides: overridesProp, showBackdrop = false, usePortal = false, ...restProps } = props;
  const overrides = mergeOverrides(TOOLTIP_OVERRIDES, overridesProp);

  // Ensure only one child
  const children = React.Children.only(childrenProp);

  const shouldPortalContent = showBackdrop || usePortal;
  const isHoverTooltip = props.triggerType === 'hover';
  // Auto-open tips without an owner close callback cannot yield to another tooltip.
  const canCoordinateHover = props.isOpen === undefined || typeof props.onClose === 'function';
  const Content = isHoverTooltip ? HoverTooltipContent : PopoverV2.Content;

  return (
    <>
      {showBackdrop && (
        <Portal appendToParentPortal>
          <Backdrop
            isOpen={!!restProps.isOpen}
            zIndex="modal"
          />
        </Portal>
      )}
      <PopoverV2
        disableDisclosureAria
        {...props}
        coordinateHover={(props.coordinateHover ?? isHoverTooltip) && canCoordinateHover}
        onMouseEnterDelay={props.onMouseEnterDelay ?? (isHoverTooltip ? 200 : undefined)}
        onMouseLeaveDelay={props.onMouseLeaveDelay ?? (isHoverTooltip ? 120 : undefined)}
        overrides={overrides}
      >
        <PopoverV2.Trigger>{children}</PopoverV2.Trigger>
        {shouldPortalContent ? (
          <Portal appendToParentPortal>
            <Content>{content}</Content>
          </Portal>
        ) : (
          <Content>{content}</Content>
        )}
      </PopoverV2>
    </>
  );
};

TooltipV2.displayName = createDisplayName('Tooltip');
TooltipV2.defaultProps = defaultProps;
